import { EnvironmentType } from 'dashboard/models/environment';
import { MfaMethod } from 'dashboard/models/user';
import { ApiListRelationship, ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

// TODO: [PER-21032] move to lib/intefaces
export enum ProductFeatureName {
  AccountsDashboard = 'accounts.dashboard',
  AccountsImporters = 'accounts.importers',
  AccountsSelfieSimilarity = 'accounts.selfie_similarity',
  BillingDashboardCurrentInvoice = 'billing.dashboard_current_invoice',
  CasesAnalytics = 'cases.analytics',
  CasesDashboard = 'cases.dashboard',
  CasesFree = 'cases.free',
  CasesBasic = 'cases.basic',
  CasesEnhanced = 'cases.enhanced',
  CasesEnterprise = 'cases.enterprise',
  CasesModulesMessages = 'cases.modules.messages',
  DevicesTrusted = 'devices.trusted',
  EnvironmentsCreate = 'environments.create',
  Exporters = 'exporters',
  // Legacy Graph - deprecated
  GraphV1 = 'graph.v1',
  // Latest and greatest graph
  Graph = 'graph',
  GraphQueryAPI = 'graph.query.api',
  InquiriesAnalytics = 'inquiries.analytics',
  InquiriesCreateApi = 'inquiries.create.api',
  InquiriesEnableDeviceFingerprint = 'inquiries.device_fingerprint',
  LegacyInquiryTemplates = 'inquiries.theme.editor',
  Importers = 'importers',
  // Inquiries product features used in ITE
  InquiriesFree = 'inquiries.free',
  InquiriesBasic = 'inquiries.basic',
  InquiriesEnhanced = 'inquiries.enhanced',
  ListsDashboard = 'lists.dashboard',
  ListsFacesUntrackableSources = 'lists.faces_untrackable_sources',
  ListsImporters = 'lists.importers',
  ListsBasic = 'lists.basic',
  ListsEnhanced = 'lists.enhanced',
  QueuesDashboard = 'queues.dashboard',
  ReportsAnalytics = 'reports.analytics',
  ReportsCreate = 'reports.create',
  ReportsPepPortraits = 'reports.pep_portraits',
  ReportsWatchlistPortraits = 'reports.watchlist_portraits',
  // New Report/Report Template product features
  ReportsWatchlistCreate = 'reports.watchlist.create',
  ReportsPoliticallyExposedPersonCreate = 'reports.politically_exposed_person.create',
  ReportsScreeningCreate = 'reports.screening.create',
  ReportsAdverseMediaCreate = 'reports.adverse_media.create',
  ReportsCryptoAddressWatchlistCreate = 'reports.crypto_address_watchlist.create',
  ReportsPhoneNumberCreate = 'reports.phone_number.create',
  ReportsEmailAddressCreate = 'reports.email_address.create',
  ReportsAddressLookupCreate = 'reports.address_lookup.create',
  ReportsBusinessWatchlistCreate = 'reports.business_watchlist.create',
  ReportsBusinessAdverseMediaCreate = 'reports.business_adverse_media.create',
  ReportsDashboardCreate = 'reports.dashboard.create',
  RolesDashboard = 'roles.dashboard',
  SARsDashboard = 'sars.dashboard',
  TransactionsDashboard = 'transactions.dashboard',
  VerificationsAnalytics = 'verifications.analytics',
  WebhooksCustomHttpHeaders = 'webhooks.custom-http-headers',
  WebhooksPayloadFilters = 'webhooks.payload-filters',
  WorkflowsDashboard = 'workflows.dashboard',
  WorkflowsFree = 'workflows.free',
  WorkflowsBasic = 'workflows.basic',
  WorkflowsEnhanced = 'workflows.enhanced',
  AccountsCreateAPI = 'accounts.create.api',
  CasesCreateAPI = 'cases.create.api',
  ReportsCreateAPI = 'reports.create.api',
  TransactionsCreateAPI = 'transactions.create.api',
  VerificationsCreateAPI = 'verifications.create.api',
  VerificationsFree = 'verifications.free',
  VerificationsBasic = 'verifications.basic',
  VerificationsEnhanced = 'verifications.enhanced',
  VerificationsGovIdCreate = 'verifications.gov_id.create',
  VerificationsSelfieCreate = 'verifications.selfie.create',
  VerificationsDatabaseUsCreate = 'verifications.database_us.create',
  VerificationsDigitalIdFinnishTrustNetwork = 'verifications.digital_id_finnish_trust_network',
  VerificationsDigitalIdOneId = 'verifications.digital_id_one_id',
  VerificationsDigitalIdSingpass = 'verifications.digital_id_singpass',
  VerificationsDigitalIdUkSharecode = 'verifications.digital_id_uk_sharecode',
  VerificationsDocumentCreate = 'verifications.document.create',
  VerificationsEmailCreate = 'verifications.email.create',
  VerificationsPhoneCreate = 'verifications.phone.create',
  VerificationsAamvaCreate = 'verifications.aamva.create',
}

export enum ProductFeatureStatus {
  Enabled = 'enabled',
  Disabled = 'disabled',
}

export interface ProductFeature {
  name: ProductFeatureName;
  status: ProductFeatureStatus;
  'environment-type': EnvironmentType;
}

export enum CustomerTier {
  Internal = 'internal',
  InternalBusinessCritical = 'internal_business_critical',
  Prospect = 'prospect',
  Tier1 = 'tier_1',
  Tier2 = 'tier_2',
  Tier2Guided = 'tier_2_guided',
  Tier2Scaled = 'tier_2_scaled',
  Tier3 = 'tier_3',
}

export enum OrganizationStatus {
  New = 'new',
  Active = 'active',
  Deactivated = 'deactivated',
}

export enum DashboardNavbarSetting {
  RoleBased = 'role_based',
  UserBased = 'user_based',
}

export interface OrganizationAttributes {
  name: string;
  status: OrganizationStatus;
  slug: string | null;
  'has-saml-enabled': boolean;
  subdomain: string | null;
  timezone: string | null;
  'billing-info': BillingInfo | null;
  'idp-metadata': IdpMetadata | null;
  'disable-required-onboarding-quiz': boolean;
  'onboarding-checklist-sandbox-completed-steps': string[];
  'allowed-hosts': string[];
  'allowed-uri-schemes': string[];
  'mfa-required-at': Iso8601DateString | null;
  'saml-required-at': Iso8601DateString | null;
  'enabled-mfa-methods': MfaMethod[];
  'product-features': ProductFeature[] | null;
  'inquiry-hosted-flow-custom-domain': string | null;
  'dashboard-navbar-setting': DashboardNavbarSetting | null;
  'retention-period-seconds': number | null;
  'customer-tier': CustomerTier | null;
  industry: string | null;

  'session-length-seconds': number | null;
  'refresh-session-on-activity': boolean;

  // onboard only
  'sso-url'?: string;
}

export interface BillingInfo {
  'card-brand': string;
  'card-funding': string;
  'card-last-4': string;
  'card-exp-year': number;
  'card-exp-month': number;
}

export interface IdpMetadata {
  'idp-entity-id': string | null;
  'idp-sso-target-url': string | null;
  'idp-slo-target-url': string | null;
  'idp-cert': string | null;
  'idp-cert-multi': Map<string, string> | null;
  'name-identifier-format': string | null;
}

export interface OrganizationRelationships {
  environments: ApiListRelationship<EnvironmentType>;
  'current-organization-plan': ApiRelationship<ApiModel.OrganizationPlan>;
  'pending-organization-plan-migration': ApiRelationship<ApiModel.OrganizationPlanMigration>;
  'theme-set': ApiRelationship<ApiModel.ThemeSet>;
}

export type Organization = ApiResource<
  OrganizationAttributes,
  OrganizationRelationships,
  ApiModel.Organization
>;
